import { useState } from 'react'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { CurrentUnitCard } from '@/components/ui/current-unit-card'
import { ChapterCompleteCard } from '@/components/ui/chapter-complete-card'
import type { LessonsCompleted } from '@/types/database'

// Mock unit data
const MOCK_UNIT = {
  id: 'test-unit-1',
  task: 'Order coffee at a cafe',
  description: 'Practice ordering drinks and snacks at a coffee shop',
}

// Mock progress states for testing
const MOCK_PROGRESS_STATES: Record<string, LessonsCompleted> = {
  noLessons: { warmup: false, vocabulary: false, grammar: false, practice: false },
  afterWarmup: { warmup: true, vocabulary: false, grammar: false, practice: false },
  afterVocab: { warmup: true, vocabulary: true, grammar: false, practice: false },
  afterGrammar: { warmup: true, vocabulary: true, grammar: true, practice: false },
  allComplete: { warmup: true, vocabulary: true, grammar: true, practice: true },
}

// Mock completed units for ChapterCompleteCard
const MOCK_COMPLETED_UNITS = [
  { id: '1', task: 'Order coffee at a cafe' },
  { id: '2', task: 'Ask for directions' },
  { id: '3', task: 'Introduce yourself' },
  { id: '4', task: 'Make a restaurant reservation' },
  { id: '5', task: 'Buy items at a store' },
  { id: '6', task: 'Describe your daily routine' },
]

type ProgressState = keyof typeof MOCK_PROGRESS_STATES

export function CurriculumTest() {
  const [currentProgressState, setCurrentProgressState] = useState<ProgressState>('noLessons')
  const [showChapterComplete, setShowChapterComplete] = useState(false)

  const progressStates = Object.keys(MOCK_PROGRESS_STATES) as ProgressState[]
  const currentIndex = progressStates.indexOf(currentProgressState)

  const handleNextState = () => {
    const nextIndex = (currentIndex + 1) % progressStates.length
    setCurrentProgressState(progressStates[nextIndex])
  }

  const handlePrevState = () => {
    const prevIndex = currentIndex === 0 ? progressStates.length - 1 : currentIndex - 1
    setCurrentProgressState(progressStates[prevIndex])
  }

  const handleStartLesson = (lessonType: string) => {
    alert(`Starting lesson: ${lessonType}`)
  }

  const handleFreeConversation = () => {
    alert('Starting free conversation')
  }

  const handleStartCheckpoint = () => {
    alert('Starting checkpoint session!')
  }

  return (
    <div className="min-h-screen bg-background p-8">
      <div className="max-w-4xl mx-auto space-y-8">
        <h1 className="text-3xl font-bold">Curriculum Components Test</h1>

        {/* Controls */}
        <Card>
          <CardHeader>
            <CardTitle>Test Controls</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center gap-4">
              <Button variant="outline" onClick={() => setShowChapterComplete(false)}>
                Show CurrentUnitCard
              </Button>
              <Button variant="outline" onClick={() => setShowChapterComplete(true)}>
                Show ChapterCompleteCard
              </Button>
            </div>

            {!showChapterComplete && (
              <div className="flex items-center gap-4">
                <Button variant="outline" onClick={handlePrevState}>
                  Previous State
                </Button>
                <span className="text-sm font-mono bg-muted px-3 py-1 rounded">
                  {currentProgressState}
                </span>
                <Button variant="outline" onClick={handleNextState}>
                  Next State
                </Button>
              </div>
            )}

            <div className="text-sm text-muted-foreground">
              {!showChapterComplete
                ? 'Cycle through different progress states to see how the CurrentUnitCard updates.'
                : 'Viewing the ChapterCompleteCard celebration state.'}
            </div>
          </CardContent>
        </Card>

        {/* Component Preview */}
        <div className="border-2 border-dashed border-muted-foreground/30 rounded-lg p-6">
          <div className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-4">
            Component Preview
          </div>

          {showChapterComplete ? (
            <ChapterCompleteCard
              chapterNumber={1}
              completedUnits={MOCK_COMPLETED_UNITS}
              onStartCheckpoint={handleStartCheckpoint}
            />
          ) : (
            <CurrentUnitCard
              unit={MOCK_UNIT}
              progress={{
                lessons_completed: MOCK_PROGRESS_STATES[currentProgressState],
              }}
              chapterProgress={{
                currentUnit: 3,
                totalUnits: 6,
                chapterNumber: 1,
              }}
              onStartLesson={handleStartLesson}
              onFreeConversation={handleFreeConversation}
            />
          )}
        </div>

        {/* State Reference */}
        <Card>
          <CardHeader>
            <CardTitle>Progress State Reference</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
              {progressStates.map((state) => (
                <Button
                  key={state}
                  variant={currentProgressState === state && !showChapterComplete ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => {
                    setCurrentProgressState(state)
                    setShowChapterComplete(false)
                  }}
                  className="text-xs"
                >
                  {state}
                </Button>
              ))}
            </div>

            <div className="mt-4 text-sm text-muted-foreground">
              <p><strong>noLessons:</strong> Fresh unit, no lessons started</p>
              <p><strong>afterWarmup:</strong> Warmup complete, vocabulary next</p>
              <p><strong>afterVocab:</strong> Vocabulary complete, grammar next</p>
              <p><strong>afterGrammar:</strong> Grammar complete, practice next</p>
              <p><strong>allComplete:</strong> All lessons complete, unit done</p>
            </div>
          </CardContent>
        </Card>

        {/* Different Chapter Numbers */}
        <Card>
          <CardHeader>
            <CardTitle>Chapter Complete Variations</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <h4 className="text-sm font-medium mb-2">Chapter 1</h4>
                <div className="scale-75 origin-top-left">
                  <ChapterCompleteCard
                    chapterNumber={1}
                    completedUnits={MOCK_COMPLETED_UNITS}
                    onStartCheckpoint={handleStartCheckpoint}
                  />
                </div>
              </div>
              <div>
                <h4 className="text-sm font-medium mb-2">Chapter 3</h4>
                <div className="scale-75 origin-top-left">
                  <ChapterCompleteCard
                    chapterNumber={3}
                    completedUnits={MOCK_COMPLETED_UNITS}
                    onStartCheckpoint={handleStartCheckpoint}
                  />
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  )
}
